import { useEffect, useState } from "react";

/**
 * Beacon Timer — tabular-nums, kein Flicker.
 * size: "inline" 24px (Header), "side" 64px (Side-Panels), "hero" 320px (Working-Mitte).
 * Letzte 2 Stellen sind in <em> mit --accent-blue-soft, kein Blink-Default.
 * Warn-Threshold (≤60s) → --signal-warn, Crit (≤10s) → --signal-error + 1Hz Blink.
 */

type TimerSize = "inline" | "side" | "hero";

interface TimerProps {
  /** Unix-Millis Deadline (Server-Zeit). */
  deadline: number;
  size?: TimerSize;
  /** Schwelle in Sekunden, ab der Color auf signal-warn wechselt. Default: 60. */
  warnAtSeconds?: number;
  /** Schwelle für signal-error + Blink. Default: 10. */
  critAtSeconds?: number;
}

function formatMmss(secondsLeft: number): { mm: string; ss: string } {
  const safe = Math.max(0, Math.floor(secondsLeft));
  const mm = Math.floor(safe / 60);
  const ss = safe % 60;
  return {
    mm: String(mm).padStart(2, "0"),
    ss: String(ss).padStart(2, "0"),
  };
}

const SIZE_TO_FONT: Record<TimerSize, string> = {
  inline: "24px",
  side: "64px",
  hero: "clamp(160px, 18vw, 320px)",
};

export function Timer({
  deadline,
  size = "inline",
  warnAtSeconds = 60,
  critAtSeconds = 10,
}: TimerProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  const secondsLeft = Math.max(0, Math.round((deadline - now) / 1000));
  const { mm, ss } = formatMmss(secondsLeft);

  const isCrit = secondsLeft <= critAtSeconds;
  const isWarn = secondsLeft <= warnAtSeconds && !isCrit;

  const color = isCrit
    ? "var(--signal-error)"
    : isWarn
      ? "var(--signal-warn)"
      : "currentColor";

  return (
    <span
      className={`beacon-timer mono${isCrit ? " is-crit" : ""}`}
      style={{
        fontFamily: "var(--font-mono)",
        fontVariantNumeric: "tabular-nums",
        fontSize: SIZE_TO_FONT[size],
        letterSpacing: "var(--tracking-display)",
        fontWeight: 500,
        color,
        display: "inline-block",
      }}
      aria-live="off"
    >
      {mm}
      <span style={{ opacity: 0.6 }}>:</span>
      <em
        style={{
          fontStyle: "normal",
          color: isCrit || isWarn ? "inherit" : "var(--accent-blue-soft)",
        }}
      >
        {ss}
      </em>
    </span>
  );
}
